import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useMutation } from "@tanstack/react-query";
import { Transaction } from "@common/types/transaction";
import { Account } from "@common/types/domains/accounting/account";
import {
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import { leafGroupHeader } from "@common/constants/domains/accounting/accountGroups";
import { displayAccountLabel } from "@common/helpers/accounting";
import { getScheduleFieldLock } from "@common/helpers/schedule-transaction-field-lock";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useTransactionLedgerAccount } from "@hooks/useTransactionLedgerAccount";
import { useLedgers } from "@api/ledger.api";
import { useAccounts } from "@hooks/useAccounts";
import { buildPrimaryLedgerOverride } from "@lib/accountOverrides";
import { Picker } from "@design-system/Picker/Picker.lib";
import { PlusIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionDetailsField } from "./TransactionDetailsField";
import styles from "./TransactionDetailsAccount.module.scss";
import { TransactionDetailsSection } from "./TransactionDetailsSection";

interface Props {
    transaction: Transaction;
    isLocked?: boolean;
    lockTooltip?: string;
    basis: AccountingLedgerBasis;
    glFirstView?: boolean;
}

export const TransactionDetailsAccount: React.FC<Props> = ({
    transaction,
    isLocked,
    lockTooltip,
    basis,
    glFirstView,
}) => {
    const showAccountNumbers = useShowAccountNumbers();
    const { onUpdate } = useContext(TransactionDetailsContext);
    const { openOverlay } = useOverlays();

    const isAccrualBasis = basis === AccountingLedgerBasis.accruals;
    const currentAccountId = isAccrualBasis
        ? (transaction.accrualAccountId ?? undefined)
        : (transaction.accountId ?? undefined);

    const {
        entityIdForGLAccount,
        account: accountToShow,
        disableAccountSelect,
    } = useTransactionLedgerAccount(transaction, {
        includeCurrentAccount: true,
        currentAccountId,
        basis,
    });

    const { data: ledgers } = useLedgers(entityIdForGLAccount);
    const ledger = useMemo(
        () => ledgers?.find((l) => l.basis === basis),
        [ledgers, basis],
    );

    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const { accounts, isLoading } = useAccounts({
        entityId: entityIdForGLAccount ?? undefined,
        excludeLinkedToFinancialAccount: true,
        includeCurrentAccount: true,
        currentAccountId: accountToShow?.id,
        ledgerBasis: basis,
    });

    const accountsById = useMemo(
        () => new Map(accounts.map((account) => [account.id, account])),
        [accounts],
    );

    const getAccountLabel = useCallback(
        (account: Account) => displayAccountLabel(account, showAccountNumbers),
        [showAccountNumbers],
    );

    const filteredAccounts = useMemo(() => {
        if (!inputValue.trim()) {
            return accounts;
        }
        const lower = inputValue.toLowerCase();
        return accounts.filter(
            (a) =>
                a.name.toLowerCase().includes(lower) ||
                (a.codeText ?? "").toLowerCase().includes(lower),
        );
    }, [accounts, inputValue]);

    const accountGroups = useMemo(() => {
        const groupMap = new Map<string, Account[]>();
        for (const account of filteredAccounts) {
            const groupKey = leafGroupHeader.keyOf(account);
            const existing = groupMap.get(groupKey);
            if (existing) {
                existing.push(account);
            } else {
                groupMap.set(groupKey, [account]);
            }
        }
        return Array.from(groupMap.entries()).map(([type, accs]) => ({
            type,
            accounts: accs,
        }));
    }, [filteredAccounts]);

    useEffect(() => {
        if (!isOpen) {
            setInputValue("");
        }
    }, [isOpen]);

    const updateAccount = useMutation({
        mutationFn: async (account: Account | null) => {
            await onUpdate(
                {
                    accountOverrides: buildPrimaryLedgerOverride(
                        ledger?.id,
                        account?.id ?? null,
                    ),
                },
                account
                    ? displayAccountLabel(account, showAccountNumbers)
                    : undefined,
            );
        },
    });

    const { mutate: mutateAccount } = updateAccount;
    const onAccountsCreated = useCallback(
        (newAccounts: Account[]) => {
            const match = newAccounts.find(
                (a) => "entityId" in a && a.entityId === entityIdForGLAccount,
            );
            if (match) {
                mutateAccount(match);
            }
        },
        [entityIdForGLAccount, mutateAccount],
    );

    const openCreateOverlay = useCallback(() => {
        setIsOpen(false);
        openOverlay<"createCoaAccount">("createCoaAccount", {
            entityIds: [entityIdForGLAccount ?? transaction.entity.id],
            accountName: inputValue ?? "",
            accountType: (accountToShow?.type ??
                AccountType.cash) as AccountType,
            accountSubtype: (accountToShow?.subtype ??
                AccountSubtype.cashOnHand) as AccountSubtype,
            onAccountsCreated,
        });
    }, [
        openOverlay,
        entityIdForGLAccount,
        transaction.entity.id,
        inputValue,
        accountToShow?.type,
        accountToShow?.subtype,
        onAccountsCreated,
    ]);

    const label = isAccrualBasis ? "Accrual ledger" : "Cash";

    const { isLocked: isScheduleLocked, tooltip: scheduleLockTooltip } =
        getScheduleFieldLock({
            schedule: isAccrualBasis ? transaction.schedule : undefined,
            ledgerId: ledger?.id,
        });
    const isDisabled = (isLocked ?? disableAccountSelect) || isScheduleLocked;
    // A period or match lock (`isLocked`) wins; only its own tooltip shows.
    const scheduleTooltip = isLocked ? undefined : scheduleLockTooltip;
    const tooltip = disableAccountSelect
        ? "Select any business category to modify Cash ledger"
        : (lockTooltip ?? scheduleTooltip);

    if (!entityIdForGLAccount || !ledger) {
        return null;
    }

    const picker = (
        <Picker.Select
            searchable
            value={accountToShow?.id ?? null}
            open={isOpen}
            onValueChange={(accountId) => {
                if (!isDisabled) {
                    updateAccount.mutate(
                        accountId === null
                            ? null
                            : (accountsById.get(accountId) ?? null),
                    );
                    setIsOpen(false);
                }
            }}
            onInputValueChange={(value) => setInputValue(value)}
            onOpenChange={(open) => {
                setIsOpen(open);
            }}
        >
            <Picker.ButtonTrigger
                render={
                    <Picker.Button
                        variant="primary"
                        size="md"
                        disabled={isDisabled}
                        className={styles.ledgerTrigger}
                    />
                }
                value={
                    accountToShow
                        ? getAccountLabel(accountToShow)
                        : "Set account"
                }
                placeholder="Set account"
                tooltip={tooltip}
                showChevron={false}
            />
            <Picker.Popup sideOffset={4}>
                <Picker.Search placeholder="Search accounts…" />
                <Picker.List>
                    {accountToShow && (
                        <Picker.Item
                            onSelect={() => {
                                if (!isDisabled) {
                                    updateAccount.mutate(null);
                                }
                            }}
                        >
                            No account
                        </Picker.Item>
                    )}
                    {isLoading && <Picker.Loading>Loading…</Picker.Loading>}
                    {!isLoading && accountGroups.length === 0 && (
                        <Picker.Empty>No matching accounts</Picker.Empty>
                    )}
                    {!isLoading &&
                        accountGroups.map((group, i) => (
                            <React.Fragment key={group.type}>
                                {i > 0 && <Picker.Separator />}
                                <Picker.Section>
                                    <Picker.SectionTitle>
                                        {leafGroupHeader.labelOf(
                                            group.accounts[0],
                                        )}
                                    </Picker.SectionTitle>
                                    {group.accounts.map((account) => (
                                        <Picker.Item
                                            key={account.id}
                                            value={account.id}
                                        >
                                            {getAccountLabel(account)}
                                        </Picker.Item>
                                    ))}
                                </Picker.Section>
                            </React.Fragment>
                        ))}
                    <Picker.Footer>
                        <Picker.Item
                            leading={<PlusIcon />}
                            onSelect={openCreateOverlay}
                        >
                            {inputValue
                                ? `Create "${inputValue}"`
                                : "Create new account"}
                        </Picker.Item>
                    </Picker.Footer>
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );

    if (glFirstView) {
        return <TransactionDetailsSection addon={picker} />;
    }

    return <TransactionDetailsField label={label} value={picker} />;
};
